// imports
import { AnyAction } from '@reduxjs/toolkit';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';

import EmptyList from './empty';
import Topbar from '../tabView/topbar';
import { TEXTS } from '../../constants';
import QuestCard from '../../atoms/card/quest';
import Dropdown from '../../atoms/dropdown/list';
import { logAnalytics } from '../../services/firebase';
import ScrollableContainer from '../../atoms/container/scrollable';
import { selectUserQuests } from '../../repositories/store/slices';
import { fetchQuestsProgress } from '../../repositories/store/thunks';
import {
  Quest, QuestStatus, IActionItem,
  IScrollableContainer, QuestCost, QuestRewards
} from '../../common/types';

// interfaces / types / consts
type QuestCB = (quest?: Quest) => void;
type QuestAmount = QuestCost | QuestRewards;
type SortBy = 'name' | 'time' | 'cost' | 'reward';
interface Props {
  pinId?: string;
  defaultTab?: string;
  selectedQuest?: Quest;
  questClickedCB?: QuestCB;
  filterChangedCB?: QuestCB;
}

// exports
export default function QuestList(props: Props): JSX.Element {
  const {
    pinId, selectedQuest, defaultTab, questClickedCB, filterChangedCB
  } = props;
  // hooks configuration
  const dispatch = useDispatch();
  const { state } = useLocation();
  const isInitialMount = useRef(true);
  const containerRef = useRef<IScrollableContainer>(null);
  const [sortOrder, setSortOrder] = useState<SortBy>('time');
  let defaultFilter: QuestStatus;
  if (defaultTab) {
    defaultFilter = defaultTab === 'in-progress' ? QuestStatus.inProgress : QuestStatus.completed;
  } else {
    defaultFilter = pinId ? QuestStatus.available : QuestStatus.inProgress;
  }
  const [statusFilter, setStatusFilter] = useState<QuestStatus>(defaultFilter);
  const { data, fetchStatus } = useSelector(selectUserQuests);

  // private methods
  const handleCB = (quest?: Quest, cb?: QuestCB): void => { if (cb) { cb(quest); } };
  const handleTabClick = (status: QuestStatus): void => {
    setStatusFilter(status);
    containerRef.current?.resetScroll();
  };
  const getTotal = (pItem: QuestAmount[]): number => pItem.reduce((total, item) => total + item.amount, 0);
  const getTabs = (showAvailable: boolean): IActionItem[] => {
    const tabs = [
      { key: 'in-progress', onClick: () => handleTabClick(QuestStatus.inProgress) },
      { key: 'completed', onClick: () => handleTabClick(QuestStatus.completed) }
    ];
    if (showAvailable) { tabs.unshift({ key: 'available', onClick: () => handleTabClick(QuestStatus.available) }); }
    return tabs;
  };
  const getAllQuests = (): Quest[] => {
    const allQuests = Object.values(data);
    return pinId ? allQuests.filter((quest) => quest.pinId === pinId) : allQuests;
  };
  const getFilteredQuests = (quests: Quest[]): Quest[] => {
    const filtered = quests.filter((quest) => {
      if (statusFilter === QuestStatus.available) {
        return quest.status === statusFilter || quest.status === QuestStatus.locked;
      }
      return quest.status === statusFilter;
    });
    if (sortOrder === 'time') {
      if (statusFilter === QuestStatus.available) {
        filtered.sort((a, b) => a.duration - b.duration);
      } else {
        filtered.sort((a, b) => ((a.endTime && b.endTime) ? a.endTime - b.endTime : 0));
      }
    } else if (sortOrder === 'name') {
      filtered.sort((a, b) => (a.name < b.name ? -1 : 0));
    } else if (sortOrder === 'cost') {
      filtered.sort((a, b) => getTotal(a.cost) - getTotal(b.cost));
    } else if (sortOrder === 'reward') {
      filtered.sort((a, b) => getTotal(b.rewards) - getTotal(a.rewards));
    }
    return filtered;
  };

  const allQuests = getAllQuests();
  const filteredQuests = getFilteredQuests(allQuests);
  const validQuests = Boolean(filteredQuests.length);
  const completedQuests = allQuests.filter((quest) => quest.status === QuestStatus.completed).length;

  useEffect(() => { // componentDidMount
    if (fetchStatus === 'idle') {
      dispatch(fetchQuestsProgress() as unknown as AnyAction);
    } else if (isInitialMount.current && fetchStatus === 'success') {
      isInitialMount.current = false;
      if (state?.lastSelectedQuestId) {
        containerRef.current?.scrollToId(`quest-card-${state.lastSelectedQuestId}`);
        handleCB(filteredQuests.find((quest) => quest.id === state.lastSelectedQuestId), filterChangedCB);
        state.lastSelectedQuestId = undefined;
      } else { handleCB(filteredQuests[0], filterChangedCB); }
    } else if (!isInitialMount.current && allQuests.find((quest) => quest.id === selectedQuest?.id)?.status !== statusFilter) {
      const locked = (filteredQuests[0]?.status === QuestStatus.locked);
      handleCB(filteredQuests[locked ? -1 : 0], filterChangedCB);
    }
  });

  let timeText = '';
  let emptyText = '';
  switch (statusFilter) {
    case (QuestStatus.available):
      timeText = 'duration';
      emptyText = TEXTS.LIST_QUESTS_AVAILABLE_EMPTY;
      break;
    case (QuestStatus.inProgress):
      timeText = 'remaining';
      emptyText = TEXTS.LIST_QUESTS_INPROGRESS_EMPTY;
      break;
    default:
      timeText = 'completion';
      emptyText = TEXTS.LIST_QUESTS_COMPLETED_EMPTY;
  }

  const sortOptions = [
    { key: 'name', onClick: () => setSortOrder('name') },
    { key: timeText, onClick: () => setSortOrder('time') }
  ];
  if (statusFilter === QuestStatus.available) {
    sortOptions.push({ key: 'cost', onClick: () => setSortOrder('cost') });
    sortOptions.push({ key: 'reward', onClick: () => setSortOrder('reward') });
  }

  // render
  return (
    <div style={{ height: '100%' }}>
      <Topbar defaultSelected={defaultTab} tabs={getTabs(Boolean(pinId))} notificationBadge={completedQuests} />
      <ScrollableContainer ref={containerRef} fixed={<Dropdown defaultSelectIdx={1} items={sortOptions} />}>
        {!validQuests ? (
          <EmptyList text={fetchStatus === 'success' ? emptyText : TEXTS.LIST_LOADING} />
        ) : (
          <div className="quest-list">
            {filteredQuests.map((quest) => {
              const { id } = quest;
              return (
                <QuestCard
                  key={id}
                  quest={quest}
                  selected={selectedQuest?.id === id}
                  onClicked={() => {
                    handleCB(quest, questClickedCB);
                    containerRef.current?.scrollToId(`quest-card-${quest?.id}`);
                    logAnalytics({ action: 'SELECT', name: 'QUEST_ITEM', eventParams: { questId: id, pinId, type: quest.type } });
                  }}
                />
              );
            })}
          </div>
        )}
      </ScrollableContainer>
    </div>
  );
}

// default props
QuestList.defaultProps = {
  pinId: '',
  defaultTab: '',
  selectedQuest: undefined,
  questClickedCB: undefined,
  filterChangedCB: undefined
};
